import { ChevronLeft } from '@mui/icons-material';
import { Box, Button, Divider, Drawer, List } from '@mui/material';
import type { FC } from 'react';
import { useMemo } from 'react';
import { usePermissions } from 'src/permissions/can';
import type { AppAbility } from 'src/permissions/define-permissions';
import { useSettingsStore } from 'src/store/settings-store';
import { ViewContext, useUserStore } from 'src/store/user-store';
import { getRoutes as siteRoutes } from 'src/view-contexts/site/constants';
import { getRoutes as tenantRoutes } from 'src/view-contexts/tenant/constants';
import { Scrollbar } from '../atoms/scrollbar';
import SidebarItems from '../molecules/sidebar/sidebar-items';

interface SidebarProps {
  onPin?: () => void;
  pinned: boolean;
}

const getRoutesForViewContext = (
  currentViewContext: ViewContext,
  permissions: AppAbility,
) => {
  switch (currentViewContext) {
    case 'tenant':
      return tenantRoutes(permissions);

    case 'site':
      return siteRoutes();
  }
};

const SidebarComponent: FC<SidebarProps> = props => {
  const { pinned } = props;
  const viewContext = useUserStore(state => state.viewContext);
  const permissions = usePermissions();

  const sections = useMemo(
    () => getRoutesForViewContext(viewContext ?? 'tenant', permissions) ?? [],
    [permissions, viewContext],
  );

  const saveSettings = useSettingsStore(state => state.saveSettings);

  return (
    <>
      <Drawer
        open
        variant="permanent"
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
            height: 'calc(100% - 64px)',
            overflowX: 'hidden',
            top: 64,
            zIndex: 1,
            transition: 'width 250ms ease-in-out',
            width: pinned ? 270 : 73,
            '& .simplebar-content': {
              height: '100%',
            },
          },
        }}>
        <Scrollbar
          style={{
            display: 'flex',
            flex: 1,
            overflowX: 'hidden',
            overflowY: 'auto',
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              p: 2,
              pt: 2.5,
              pb: 7,
            }}>
            <List
              disablePadding
              sx={{
                mt: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}>
              {sections.map(section => (
                <SidebarItems
                  key={section.title}
                  title={section.title}
                  routes={section.routes}
                  pinned={pinned}
                />
              ))}
            </List>
          </Box>
        </Scrollbar>

        <Box
          sx={{
            padding: pinned ? 2 : '16px 0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: pinned ? 'start' : 'center',
            gap: 2,
            marginBottom: 2,
            transition: 'all 250ms ease-in-out',
          }}>
          <Divider sx={{ width: '100%' }} />

          <Button
            sx={{
              color: 'text.primary',
              '&:hover': {
                color: 'text.primary',
              },
              '&:active': {
                color: 'text.primary',
              },
              '&:focus': {
                color: 'text.primary',
              },
              minWidth: 0,
            }}
            onClick={() => {
              saveSettings({ pinSidebar: !pinned });
            }}>
            <ChevronLeft
              sx={{
                transform: pinned ? '' : 'rotate(180deg)',
                transition: 'all 250ms ease-in-out',
              }}
            />
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export const Sidebar = SidebarComponent;
